import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '@/components/ui/table';
import { ChevronDown, ChevronUp, Search } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useAIEvaluations } from '../ai-tracer-integration/hooks/useAITracerData';
import type { LLMEvaluation } from '../ai-tracer-integration/types/ai-tracer-types';
import {
	EvaluationTabContent,
	TabbedDetailModal,
	TraceTabContent,
} from './components/tabbed-detail-modal';
import {
	DateRangeFilter,
	calculateDateRange,
} from './shared/date-range-filter';
import { PaginationControls } from './shared/pagination-controls';

type SortableFields =
	| Exclude<keyof LLMEvaluation, 'feedback' | 'metadata' | 'categories'>
	| 'accuracy'
	| 'relevance'
	| 'coherence'
	| 'safety'
	| 'app'
	| 'function';
type SortDirection = 'asc' | 'desc';

interface LLMEvaluationsProps {
	environmentFilter?: string;
}

export const LLMEvaluations = ({ environmentFilter }: LLMEvaluationsProps) => {
	// Date range filtering state
	const [dateRange, setDateRange] = useState<string>('30d'); // Default to past 30 days
	// Calculate initial date range immediately (30 days back from today)
	const initialDateRange = calculateDateRange(30);
	const [dateFrom, setDateFrom] = useState<string | undefined>(
		initialDateRange.dateFrom,
	);
	const [dateTo, setDateTo] = useState<string | undefined>(
		initialDateRange.dateTo,
	);

	const {
		evaluations,
		isLoading,
		isLoadingMore,
		error,
		chunksLoaded,
		totalChunks,
	} = useAIEvaluations(environmentFilter, 1000, dateFrom, dateTo);
	const [selectedEvaluation, setSelectedEvaluation] =
		useState<LLMEvaluation | null>(null);
	const [currentPage, setCurrentPage] = useState(0);
	const [pageSize, setPageSize] = useState(10); // Show 10 per page with pagination
	const [globalFilter, setGlobalFilter] = useState('');
	const [appFilter, setAppFilter] = useState<string>('');
	const [sortField, setSortField] = useState<SortableFields>('timestamp');
	const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
	const [copiedEvaluationId, setCopiedEvaluationId] = useState<string | null>(
		null,
	);

	// Extract app name from connected trace metadata
	const getAppName = (evaluation: LLMEvaluation) => {
		if (
			evaluation.trace?.metadata &&
			typeof evaluation.trace.metadata === 'object'
		) {
			const metadata = evaluation.trace.metadata as Record<string, unknown>;
			return typeof metadata.app === 'string' ? metadata.app : 'Unknown';
		}
		return 'Unknown';
	};

	// Extract function name from connected trace
	const getFunctionName = (evaluation: LLMEvaluation) => {
		if (evaluation.trace?.function_id) {
			return evaluation.trace.function_id;
		}
		return 'Unknown';
	};

	// Filter, sort and paginate data
	const { filteredData, paginatedData, totalPages } = useMemo(() => {
		if (isLoading || !evaluations.length) {
			return { filteredData: [], paginatedData: [], totalPages: 0 };
		}

		// Apply filters - search across all relevant visible column/cell content
		const filtered = evaluations.filter((evaluation) => {
			if (globalFilter === '') {
				// No global filter applied
				const matchesAppFilter =
					appFilter === '' || getAppName(evaluation) === appFilter;
				return matchesAppFilter;
			}

			const searchTerm = globalFilter.toLowerCase();

			// Search only in visible column content using EXACT same formatting as displayed in table cells
			const searchableFields = [
				formatDate(evaluation.timestamp), // Timestamp - formatted same as display
				evaluation.trace_id, // Trace ID - as displayed (raw value)
				getAppName(evaluation), // App name from trace metadata
				getFunctionName(evaluation), // Function name (revbot, askrev, etc.)
				evaluation.category, // Category - as displayed (raw value)
				evaluation.overall_score.toFixed(1), // Overall Score - formatted same as ScoreSparkLine display
				evaluation.categories.accuracy.toFixed(1), // Accuracy - formatted same as ScoreSparkLine display
				evaluation.categories.relevance.toFixed(1), // Relevance - formatted same as ScoreSparkLine display
				evaluation.categories.coherence.toFixed(1), // Coherence - formatted same as ScoreSparkLine display
				evaluation.categories.safety.toFixed(1), // Safety - formatted same as ScoreSparkLine display
			];

			const matchesGlobalFilter = searchableFields.some((field) =>
				field.toLowerCase().includes(searchTerm),
			);

			const matchesAppFilter =
				appFilter === '' || getAppName(evaluation) === appFilter;

			return matchesGlobalFilter && matchesAppFilter;
		});

		// Sort the filtered data
		const sorted = [...filtered].sort((a, b) => {
			let aValue: string | number;
			let bValue: string | number;

			// Handle special cases for sorting
			if (sortField === 'timestamp') {
				aValue = new Date(a.timestamp).getTime();
				bValue = new Date(b.timestamp).getTime();
			} else if (sortField === 'accuracy') {
				aValue = a.categories.accuracy;
				bValue = b.categories.accuracy;
			} else if (sortField === 'relevance') {
				aValue = a.categories.relevance;
				bValue = b.categories.relevance;
			} else if (sortField === 'coherence') {
				aValue = a.categories.coherence;
				bValue = b.categories.coherence;
			} else if (sortField === 'safety') {
				aValue = a.categories.safety;
				bValue = b.categories.safety;
			} else if (sortField === 'app') {
				aValue = getAppName(a).toLowerCase();
				bValue = getAppName(b).toLowerCase();
			} else if (sortField === 'function') {
				aValue = getFunctionName(a).toLowerCase();
				bValue = getFunctionName(b).toLowerCase();
			} else {
				const rawAValue = a[sortField as keyof LLMEvaluation];
				const rawBValue = b[sortField as keyof LLMEvaluation];

				// Handle different field types safely
				if (typeof rawAValue === 'string' && typeof rawBValue === 'string') {
					aValue = rawAValue.toLowerCase();
					bValue = rawBValue.toLowerCase();
				} else if (
					typeof rawAValue === 'number' &&
					typeof rawBValue === 'number'
				) {
					aValue = rawAValue;
					bValue = rawBValue;
				} else {
					// Fallback to string representation for other types
					aValue = String(rawAValue || '').toLowerCase();
					bValue = String(rawBValue || '').toLowerCase();
				}
			}

			if (aValue < bValue) {
				return sortDirection === 'asc' ? -1 : 1;
			}
			if (aValue > bValue) {
				return sortDirection === 'asc' ? 1 : -1;
			}
			return 0;
		});

		// Pagination
		const startIndex = currentPage * pageSize;
		const endIndex = startIndex + pageSize;
		const paginated = sorted.slice(startIndex, endIndex);

		return {
			filteredData: sorted,
			paginatedData: paginated,
			totalPages: Math.ceil(sorted.length / pageSize),
		};
	}, [
		isLoading,
		evaluations,
		globalFilter,
		appFilter,
		currentPage,
		pageSize,
		sortField,
		sortDirection,
	]);

	const formatDate = (dateString: string) => {
		return new Date(dateString).toLocaleString();
	};

	const ScoreSparkLine = ({
		score,
		maxScore = 10,
	}: { score: number; maxScore?: number }) => {
		const percentage = Math.min(Math.max(score / maxScore, 0), 1) * 100; // Convert to percentage

		// Determine color based on performance
		let fillColor: string;
		if (percentage >= 80) {
			fillColor = '#10b981'; // green-500
		} else if (percentage >= 60) {
			fillColor = '#3b82f6'; // blue-500
		} else {
			fillColor = '#ef4444'; // red-500
		}

		return (
			<div className='tw-flex tw-items-center tw-gap-2'>
				<span className='tw-text-sm tw-font-medium tw-min-w-[32px]'>
					{score.toFixed(1)}
				</span>
				<div className='tw-relative tw-w-12 tw-h-4 tw-bg-gray-200 tw-rounded'>
					<div
						className='tw-h-full tw-rounded tw-transition-all tw-duration-300'
						style={{
							width: `${percentage}%`,
							backgroundColor: fillColor,
						}}
					/>
				</div>
			</div>
		);
	};

	const handleSort = (field: SortableFields) => {
		if (sortField === field) {
			// Toggle direction if same field
			setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
		} else {
			// New field, default to desc for scores/timestamp, asc for text fields
			setSortField(field);
			setSortDirection(
				['id', 'trace_id', 'category', 'app', 'function'].includes(field)
					? 'asc'
					: 'desc',
			);
		}
		setCurrentPage(0); // Reset to first page when sorting
	};

	const handleAppClick = (appName: string) => {
		setAppFilter(appName);
		setCurrentPage(0); // Reset to first page when filtering
	};

	const clearAppFilter = () => {
		setAppFilter('');
		setCurrentPage(0);
	};

	const handleDateRangeChange = (
		range: string,
		dateFrom?: string,
		dateTo?: string,
	) => {
		setDateRange(range);
		setDateFrom(dateFrom);
		setDateTo(dateTo);
		setCurrentPage(0); // Reset pagination when date range changes
	};

	const handleCopyTraceId = async (traceId: string, evaluationId: string) => {
		try {
			await navigator.clipboard.writeText(traceId);
			setCopiedEvaluationId(evaluationId);
			// Reset after 1.5 seconds
			setTimeout(() => setCopiedEvaluationId(null), 1500);
		} catch (error) {
			console.error('Failed to copy trace ID:', error);
		}
	};

	// Calculate averages for the current page of data
	const calculateAverages = (data: LLMEvaluation[]) => {
		if (data.length === 0) {
			return {
				overall_score: 0,
				accuracy: 0,
				relevance: 0,
				coherence: 0,
				safety: 0,
			};
		}

		const totals = data.reduce(
			(acc, evaluation) => ({
				overall_score: acc.overall_score + evaluation.overall_score,
				accuracy: acc.accuracy + evaluation.categories.accuracy,
				relevance: acc.relevance + evaluation.categories.relevance,
				coherence: acc.coherence + evaluation.categories.coherence,
				safety: acc.safety + evaluation.categories.safety,
			}),
			{
				overall_score: 0,
				accuracy: 0,
				relevance: 0,
				coherence: 0,
				safety: 0,
			},
		);

		return {
			overall_score: totals.overall_score / data.length,
			accuracy: totals.accuracy / data.length,
			relevance: totals.relevance / data.length,
			coherence: totals.coherence / data.length,
			safety: totals.safety / data.length,
		};
	};

	const averages = calculateAverages(paginatedData);

	const SortableHeader = ({
		field,
		children,
		className = '',
	}: {
		field: SortableFields;
		children: React.ReactNode;
		className?: string;
	}) => {
		const isActive = sortField === field;
		const direction = isActive ? sortDirection : null;

		return (
			<TableHead
				className={`${className} tw-cursor-pointer hover:tw-bg-gray-100 tw-select-none`}
				onClick={() => handleSort(field)}
			>
				<div className='tw-flex tw-items-center tw-justify-center tw-gap-1'>
					{children}
					<div className='tw-flex tw-flex-col'>
						<ChevronUp
							className={`tw-h-3 tw-w-3 ${isActive && direction === 'asc' ? 'tw-text-blue-600' : 'tw-text-gray-300'}`}
						/>
						<ChevronDown
							className={`tw-h-3 tw-w-3 -tw-mt-1 ${isActive && direction === 'desc' ? 'tw-text-blue-600' : 'tw-text-gray-300'}`}
						/>
					</div>
				</div>
			</TableHead>
		);
	};

	// Handle GraphQL errors
	if (error) {
		return (
			<div className='tw-space-y-4'>
				<div className='tw-bg-red-50 tw-border tw-border-red-200 tw-rounded-md tw-p-4'>
					<div className='tw-flex'>
						<div className='tw-ml-3'>
							<h3 className='tw-text-sm tw-font-medium tw-text-red-800'>
								Error loading AI evaluations
							</h3>
						</div>
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className='tw-space-y-4'>
			<div className='tw-flex tw-flex-col sm:tw-flex-row tw-gap-4 tw-items-center tw-justify-between'>
				<div className='tw-flex tw-gap-4 tw-flex-1'>
					<div className='tw-relative tw-w-full sm:tw-w-72'>
						<Search className='tw-absolute tw-left-2 tw-top-2.5 tw-h-4 tw-w-4 tw-text-muted-foreground' />
						<Input
							placeholder='Search evaluations...'
							value={globalFilter ?? ''}
							onChange={(e) => setGlobalFilter(e.target.value)}
							className='tw-pl-8'
						/>
					</div>
					{appFilter && (
						<div className='tw-flex tw-items-center tw-gap-2 tw-bg-purple-50 tw-px-3 tw-py-1 tw-rounded-md'>
							<span className='tw-text-sm tw-text-purple-700'>
								App: {appFilter}
							</span>
							<Button
								variant='ghost'
								size='sm'
								onClick={clearAppFilter}
								className='tw-h-6 tw-w-6 tw-p-0 tw-text-purple-600 hover:tw-text-purple-800'
							>
								✕
							</Button>
						</div>
					)}
				</div>
				<div className='tw-text-sm tw-text-muted-foreground tw-flex tw-items-center tw-gap-2'>
					{filteredData.length} evaluations available
					{isLoadingMore && totalChunks > 0 && (
						<span className='tw-text-blue-600 tw-text-xs'>
							Loading... ({chunksLoaded}/{totalChunks} chunks)
						</span>
					)}
				</div>
			</div>

			{/* Date Range Filter */}
			<DateRangeFilter
				selectedRange={dateRange}
				onRangeChange={handleDateRangeChange}
				className='tw-border-t tw-pt-4'
			/>

			<div className='tw-rounded-md tw-border tw-w-full overflow-x-auto'>
				<Table>
					<TableHeader>
						<TableRow>
							<SortableHeader
								field='timestamp'
								className='tw-text-center tw-font-medium'
							>
								Timestamp
							</SortableHeader>
							<SortableHeader
								field='trace_id'
								className='tw-text-center tw-font-medium'
							>
								Trace ID
							</SortableHeader>
							<SortableHeader
								field='app'
								className='tw-text-center tw-font-medium'
							>
								App
							</SortableHeader>
							<SortableHeader
								field='function'
								className='tw-text-center tw-font-medium'
							>
								Function
							</SortableHeader>
							<SortableHeader
								field='category'
								className='tw-text-center tw-font-medium'
							>
								Category
							</SortableHeader>
							<SortableHeader
								field='overall_score'
								className='tw-text-center tw-font-medium'
							>
								Overall Score
							</SortableHeader>
							<SortableHeader
								field='accuracy'
								className='tw-text-center tw-font-medium'
							>
								Accuracy
							</SortableHeader>
							<SortableHeader
								field='relevance'
								className='tw-text-center tw-font-medium'
							>
								Relevance
							</SortableHeader>
							<SortableHeader
								field='coherence'
								className='tw-text-center tw-font-medium'
							>
								Coherence
							</SortableHeader>
							<SortableHeader
								field='safety'
								className='tw-text-center tw-font-medium'
							>
								Safety
							</SortableHeader>
						</TableRow>
					</TableHeader>
					<TableBody>
						{isLoading ? (
							Array.from({ length: pageSize }).map((_, index) => (
								<TableRow key={`loading-${index}`}>
									{Array.from({ length: 10 }).map((_, cellIndex) => (
										<TableCell key={`loading-cell-${cellIndex}`}>
											<div className='tw-animate-pulse tw-bg-gray-200 tw-h-4 tw-rounded' />
										</TableCell>
									))}
								</TableRow>
							))
						) : paginatedData.length > 0 ? (
							paginatedData.map((evaluation) => (
								<TableRow
									key={evaluation.id}
									className='tw-cursor-pointer hover:tw-bg-gray-50'
									onClick={() => setSelectedEvaluation(evaluation)}
								>
									<TableCell className='tw-text-center'>
										{formatDate(evaluation.timestamp)}
									</TableCell>
									<TableCell className='tw-text-center'>
										<Button
											variant='ghost'
											size='sm'
											className={`tw-px-1 tw-py-0 tw-h-auto tw-text-xs tw-font-mono tw-border tw-border-transparent tw-transition-all tw-duration-200 tw-max-w-20 tw-truncate ${
												copiedEvaluationId === evaluation.id
													? 'tw-text-green-700 tw-bg-green-100 hover:tw-bg-green-200 tw-border-green-300'
													: 'tw-text-blue-600 hover:tw-text-blue-800 hover:tw-bg-blue-50 hover:tw-border-blue-200'
											}`}
											onClick={(e) => {
												e.stopPropagation();
												handleCopyTraceId(evaluation.trace_id, evaluation.id);
											}}
											title={
												copiedEvaluationId === evaluation.id
													? 'Copied!'
													: 'Click to copy trace ID'
											}
										>
											{copiedEvaluationId === evaluation.id ? (
												<span className='tw-flex tw-items-center tw-gap-1'>
													<span>✓</span>
													<span>Copied!</span>
												</span>
											) : (
												evaluation.trace_id
											)}
										</Button>
									</TableCell>
									<TableCell className='tw-text-center'>
										<Button
											variant='ghost'
											size='sm'
											className='tw-px-2 tw-py-1 tw-h-auto tw-text-xs tw-font-medium tw-bg-purple-100 tw-text-purple-800 hover:tw-bg-purple-200 hover:tw-text-purple-900 tw-border tw-border-transparent hover:tw-border-purple-300 tw-transition-all tw-duration-200 tw-rounded'
											onClick={(e) => {
												e.stopPropagation();
												handleAppClick(getAppName(evaluation));
											}}
											title={`Click to filter by app: ${getAppName(evaluation)}`}
										>
											{getAppName(evaluation)}
										</Button>
									</TableCell>
									<TableCell className='tw-text-center'>
										<span
											className={`tw-px-2 tw-py-1 tw-rounded tw-text-xs tw-font-medium ${
												getFunctionName(evaluation) === 'askrev'
													? 'tw-bg-blue-100 tw-text-blue-800'
													: getFunctionName(evaluation) === 'revbot'
														? 'tw-bg-green-100 tw-text-green-800'
														: getFunctionName(evaluation) === 'autopilot'
															? 'tw-bg-purple-100 tw-text-purple-800'
															: 'tw-bg-gray-100 tw-text-gray-800'
											}`}
										>
											{getFunctionName(evaluation)}
										</span>
									</TableCell>
									<TableCell className='tw-text-center'>
										{evaluation.category}
									</TableCell>
									<TableCell className='tw-text-center'>
										<ScoreSparkLine score={evaluation.overall_score} />
									</TableCell>
									<TableCell className='tw-text-center'>
										<ScoreSparkLine score={evaluation.categories.accuracy} />
									</TableCell>
									<TableCell className='tw-text-center'>
										<ScoreSparkLine score={evaluation.categories.relevance} />
									</TableCell>
									<TableCell className='tw-text-center'>
										<ScoreSparkLine score={evaluation.categories.coherence} />
									</TableCell>
									<TableCell className='tw-text-center'>
										<ScoreSparkLine score={evaluation.categories.safety} />
									</TableCell>
								</TableRow>
							))
						) : (
							<TableRow>
								<TableCell colSpan={10} className='tw-h-24 tw-text-center'>
									No evaluations found
								</TableCell>
							</TableRow>
						)}
					</TableBody>
					{paginatedData.length > 0 && (
						<tfoot className='tw-bg-gray-50 tw-border-t tw-border-gray-200'>
							<tr>
								<td className='tw-text-center tw-py-3 tw-px-4 tw-font-medium tw-text-gray-700'>
									Page Averages
								</td>
								<td className='tw-text-center tw-py-3 tw-px-4 tw-text-sm tw-text-gray-500'>
									—
								</td>
								<td className='tw-text-center tw-py-3 tw-px-4 tw-text-sm tw-text-gray-500'>
									—
								</td>
								<td className='tw-text-center tw-py-3 tw-px-4 tw-text-sm tw-text-gray-500'>
									—
								</td>
								<td className='tw-text-center tw-py-3 tw-px-4 tw-text-sm tw-text-gray-500'>
									—
								</td>
								<td className='tw-text-center tw-py-3 tw-px-4'>
									<ScoreSparkLine score={averages.overall_score} />
								</td>
								<td className='tw-text-center tw-py-3 tw-px-4'>
									<ScoreSparkLine score={averages.accuracy} />
								</td>
								<td className='tw-text-center tw-py-3 tw-px-4'>
									<ScoreSparkLine score={averages.relevance} />
								</td>
								<td className='tw-text-center tw-py-3 tw-px-4'>
									<ScoreSparkLine score={averages.coherence} />
								</td>
								<td className='tw-text-center tw-py-3 tw-px-4'>
									<ScoreSparkLine score={averages.safety} />
								</td>
							</tr>
						</tfoot>
					)}
				</Table>
			</div>

			<PaginationControls
				currentPage={currentPage}
				totalPages={totalPages}
				pageSize={pageSize}
				totalItems={filteredData.length}
				displayedItems={paginatedData.length}
				onPageChange={setCurrentPage}
				onPageSizeChange={(newPageSize) => {
					setPageSize(newPageSize);
					setCurrentPage(0);
				}}
				itemName='evaluations'
			/>

			<TabbedDetailModal
				isOpen={!!selectedEvaluation}
				onClose={() => setSelectedEvaluation(null)}
				title='Evaluation Details'
				tabs={
					selectedEvaluation
						? (() => {
								const tabs = [
									{
										id: 'evaluation',
										label: 'Evaluation',
										content: (
											<EvaluationTabContent evaluation={selectedEvaluation} />
										),
									},
								];

								// Add trace tab if trace data is available
								const evaluationWithTrace =
									selectedEvaluation as LLMEvaluation & {
										trace?: import('./context/llm-data-context').LLMTrace;
									};
								if (evaluationWithTrace.trace) {
									tabs.push({
										id: 'trace',
										label: 'Trace',
										content: (
											<TraceTabContent trace={evaluationWithTrace.trace} />
										),
									});
								}

								return tabs;
							})()
						: []
				}
			/>
		</div>
	);
};
